import { useAuth } from '@/features/auth/stores/auth.store';
import { firestore as db } from '@/shared/lib/firebase';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { doc, onSnapshot } from 'firebase/firestore';
import { useEffect } from 'react';
import { usePricingConfig } from '../queries/pricingQueries';
import { SubscriptionData } from '../types';

export const subscriptionKeys = {
    all: ['subscription'] as const,
    user: (uid: string) => [...subscriptionKeys.all, uid] as const,
};

export const useSubscription = () => {
    const { user } = useAuth();
    const queryClient = useQueryClient();
    const { data: pricingConfig } = usePricingConfig();

    // The query acts as the reactive data store
    const { data, isLoading } = useQuery<{
        subscription: SubscriptionData | null;
        creditBalance: number;
    }>({
        queryKey: subscriptionKeys.user(user?.uid || ''),
        queryFn: () => ({ subscription: null, creditBalance: 0 }), // Initial fallback
        enabled: !!user,
        staleTime: Infinity,
    });

    useEffect(() => {
        if (!user) return;

        const userDocRef = doc(db, 'users', user.uid);
        const unsubscribe = onSnapshot(
            userDocRef,
            (docSnapshot) => {
                if (docSnapshot.exists()) {
                    const userData = docSnapshot.data();
                    const creditBalance = userData.creditBalance || 0;
                    const pendingPurchase = userData.pending_purchase || null;

                    let subscription: SubscriptionData | null = null;
                    
                    if (userData.subscription) {
                        const subData = userData.subscription;
                        const status = subData.status;
                        
                        // Use active plan ID for maxCredits if available
                        const displayPlanId = subData.planId || subData.plan || 'starter';
                        
                        let maxCredits = 100;
                        if (pricingConfig) {
                            const plan = pricingConfig.plans?.find((p: any) => p.id === displayPlanId);
                            if (plan && plan.credits) maxCredits = plan.credits;
                        }

                        subscription = {
                            ...subData,
                            creditBalance,
                            maxCredits: userData.maxCredits || maxCredits,
                            pendingPurchase // Include pending purchase info
                        } as SubscriptionData;
                    } else if (pendingPurchase) {
                        // If no active sub but there's a pending purchase
                        subscription = {
                            status: 'created',
                            creditBalance,
                            maxCredits: 100, // Default for starter
                            ...pendingPurchase
                        } as SubscriptionData;
                    }

                    // Update the React Query cache
                    queryClient.setQueryData(subscriptionKeys.user(user.uid), {
                        subscription,
                        creditBalance,
                    });
                } else {
                    queryClient.setQueryData(subscriptionKeys.user(user.uid), {
                        subscription: null,
                        creditBalance: 0,
                    });
                }
            },
            (error) => {
                console.error('Error fetching subscription:', error);
            }
        );

        return () => unsubscribe();
    }, [user, pricingConfig, queryClient]);

    const subscription = data?.subscription || null;
    const creditBalance = data?.creditBalance || 0;
    const isActive = subscription?.status === 'active';
    const isPending = subscription?.status === 'created' || !!subscription?.pendingPurchase;

    return {
        subscription,
        creditBalance,
        isActive,
        isPending,
        loading: isLoading || !pricingConfig,
        pricingConfig
    };
};
